import { Button } from "@/components/elements/Button";
import { background, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/system";

export interface ErrorContentProps {
  /** Primary heading — e.g. "Failed to load page", "404", "500" */
  title: string;
  /** Sub-heading — short description of the error */
  subtitle?: string;
  /** Detailed message telling the user what happened */
  message: string;
  /** Label for the action button */
  buttonLabel: string;
  /** Action to perform on button click — e.g. reload or navigate home */
  onAction: () => void;
}

const StyledContainer = styled(Box)({
  backgroundColor: background.primary,
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledCard = styled(Stack)({
  textAlign: "center",
  padding: spacing.lg,
  backgroundColor: background.white,
  borderRadius: rounded.sm,
  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  alignItems: "center",
  gap: 8,
});

const StyledMessage = styled(Typography)({
  color: text.secondary,
  whiteSpace: "pre-line",
});

/**
 * General-purpose error page — used by RouteErrorFallback,
 * ServerErrorPage, and PageNotFound. Supports both reload (onAction) and
 * navigation (href) button modes.
 */
export const ErrorContent = ({ title, subtitle, message, buttonLabel, onAction }: ErrorContentProps) => (
  <StyledContainer>
    <StyledCard>
      {/* Large status code or short title */}
      <Typography variant="head32Bold">{title}</Typography>
      {subtitle && <Typography variant="head20Bold">{subtitle}</Typography>}
      <StyledMessage variant="body14">{message}</StyledMessage>
      <Button onClick={onAction}>{buttonLabel}</Button>
    </StyledCard>
  </StyledContainer>
);
